import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { saveAs } from 'file-saver';
import { SalesRep } from 'src/app/core/models/sales-rep.model';
import { BoardStatsReportService } from 'src/app/core/services/board-states-report.service';

@Component({
  selector: 'app-board-stats-report',
  templateUrl: './board-stats-report.component.html',
  styleUrls: ['./board-stats-report.component.scss']
})
export class BoardStatsReportComponent implements OnInit {
  reportForm: FormGroup;
  loading = false;
  error: string | null = null;
  boardStats: any[] = [];
  salesReps: SalesRep[] = [];
  standardGameNames = [
    'RED BLAZE',
    'GOLD LINK',
    'KLASSIC KRAZE',
    'DIAMOND BLAST LINK',
    'SILVER LINK',
    'PLATINUM BLITZ'
  ];

  constructor(
    private fb: FormBuilder,
    private boardStatsService: BoardStatsReportService,  public activeModal: NgbActiveModal
  ) {
    this.reportForm = this.fb.group({
      startDate: [''],
      endDate: [''],
      selectedSalesRep: [''],
      gameName: [''],
      boardAssetTag: [''],
      groupByBoard: [false]
    });
  }

  ngOnInit(): void {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    
    this.reportForm.patchValue({
      startDate: firstDayOfMonth.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    });

    this.loadSalesReps();
    this.loadReport();
  }

  loadSalesReps(): void {
    this.boardStatsService.getSalesReps().subscribe({
      next: (reps) => {
        this.salesReps = reps.filter(rep => rep.enabled);
      },
      error: (error) => {
        console.error('Failed to load sales reps:', error);
        this.error = 'Failed to load sales representatives.';
      }
    });
  }

  loadReport(): void {
    if (this.reportForm.valid) {
      this.loading = true;
      this.error = null;
      const formValue = this.reportForm.value;

      const selectedRep = this.salesReps.find(rep => rep.id === formValue.selectedSalesRep);
      const selectedReps = selectedRep ? [selectedRep] : [];

      this.boardStatsService.getBoardStatsReport(
        new Date(formValue.startDate),
        new Date(formValue.endDate),
        selectedReps,
        formValue.gameName || undefined,
        formValue.boardAssetTag || undefined,
        formValue.groupByBoard
      ).subscribe({
        next: (data) => {
          this.boardStats = data;
          this.loading = false;
        },
        error: (error) => {
          console.error('Failed to load report:', error);
          this.error = 'Failed to load report. Please try again later.';
          this.loading = false;
        }
      });
    }
  }

  setPresetDates(preset: 'ytd' | 'mtd' | 'wtd'): void {
    const today = new Date();
    let startDate: Date;

    switch (preset) {
      case 'ytd':
        startDate = new Date(today.getFullYear(), 0, 1);
        break;
      case 'mtd':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'wtd':
        const currentDay = today.getDay();
        startDate = new Date(today);
        startDate.setDate(today.getDate() - currentDay);
        break;
      default:
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
    }

    startDate.setHours(0, 0, 0, 0);
    today.setHours(23, 59, 59, 999);

    this.reportForm.patchValue({
      startDate: startDate.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    });

    this.loadReport();
  }

  exportReport(): void {
    if (this.reportForm.valid) {
      this.loading = true;
      this.error = null;
      const formValue = this.reportForm.value;

      const selectedRep = this.salesReps.find(rep => rep.id === formValue.selectedSalesRep);
      const selectedReps = selectedRep ? [selectedRep] : [];

      const startDate = new Date(formValue.startDate);
      startDate.setHours(0, 0, 0, 0);
      
      const endDate = new Date(formValue.endDate);
      endDate.setHours(23, 59, 59, 999);

      this.boardStatsService.exportToCsv(
        startDate,
        endDate,
        selectedReps,
        formValue.gameName || undefined,
        formValue.boardAssetTag || undefined,
        formValue.groupByBoard
      ).subscribe({
        next: (blob) => {
          if (blob === null) {
            this.error = 'No data to export - all values are zero';
            this.loading = false;
            return;
          }
          const fileName = `board-stats-report-${new Date().toISOString().split('T')[0]}.csv`;
          saveAs(blob, fileName);
          this.loading = false;
        },
        error: (error) => {
          console.error('Export failed:', error);
          this.error = 'Failed to export report. Please try again later.';
          this.loading = false;
        }
      });
    }
  }
}